/* eslint react/no-unused-prop-types: 0 */
/* Eslint can't pick up usage of props, disabling the rule */

import React, { useCallback, useContext, useState } from 'react'
import Resx from '@/components/Resx'
import { Input, NumericInput, CheckBox } from '@/components/inputs'
import { DropDownList } from '@/components/dropDownList'
import { DatePicker } from '@/components/DatePicker'
import { actionCreators, getDataPath } from '@/store/Forms'
import Name from '@/components/Name'
import classNames from 'classnames'
import { Map, List } from 'immutable'
import { BsrsGrid, actionButtonsColumnProps } from '@/components/BsrsGrid'
import { GridColumn as Column } from '@progress/kendo-react-grid'
import { Validator, ServerValidator } from '@/components/validation'
import getEnumTranslation from '@/enums/enums'
import { actionCreators as dialogActions } from '@/store/Dialogs'
import { EnumDropDownList } from '@/components/enumDropDownList'
import { FormContext } from './formContext'
import uuid from 'uuid'
import Icon from '@/components/Icon'
import { formatDate } from '@/utils/dateUtilities'
import { formatNumber } from '@/utils/numberUtilities'
import {
  createModificationInfoSourceCell,
  createModificationInfoCell
} from '@/components/changeRequests/modificationInfoCell'
import { createApprovalStatusCell } from '@/components/changeRequests/approvalStatusCell'
import PermissionRequired from '@/components/auth/PermissionRequired'
import { changeChangeItemIcon } from './icons'
import { useRefMemo } from '@/hooks/useRefMemo'
import { useDispatch } from '@/hooks/dispatch'
import { useReduxState } from '@/hooks/reduxState'
import isEqual from 'lodash/isEqual'
import { UploadAttachments } from '@/components/changeRequests/fileAttachments'
import { useApprovalInfo } from '@/hooks/useApprovalInfo'
import { NullableBooleanDropDownList } from '@/components/nullableBooleanDropDownList'
import context from '@/context'
import { isModified } from './common/isModified'
import { isDefined } from '@/utils/isDefined'
import { Approval } from '@/components/Approval'
import valueFallbackComparer from '@/utils/valueComparer'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { useLocalization } from '@/hooks/localization'
import { MultiIntegerInput } from '@/components/multiIntegerInput'
import { PermissionSectionContext } from './permissionSectionContext'
import PropTypes from 'prop-types'

const EMPTY_VALUE = ''

const markAsFileAttachment = (x) => {
  if (!x.hasOwnProperty('__IS_FILEATTACHMENT')) {
    x.__IS_FILEATTACHMENT = true
    x.contentType = x.type || 'application/octet-stream'
  }
  return x
}

export const modes = {
  view: 'view', // readonly change item
  edit: 'edit', // editable change item,
  approval: 'approval'
}

const inputTypes = {
  text: 'text',
  textarea: 'textarea',
  number: 'number',
  float: 'float',
  date: 'date',
  datetime: 'datetime',
  lookup: 'lookup',
  dropdown: 'dropdown',
  enum: 'enum',
  switch: 'switch',
  checkbox: 'checkbox',
  grid: 'grid',
  attachments: 'attachments',
  nullableboolean: 'nullableboolean',
  multiinteger: 'multiinteger',
  custom: 'custom'
}

const RendererPropTypes = {
  value: PropTypes.any,
  originalValue: PropTypes.any,
  approvedValue: PropTypes.any,
  target: PropTypes.string,
  mode: PropTypes.string,
  inputType: PropTypes.oneOf(['text', 'textarea', 'number', 'float', 'date', 'datetime', 'lookup', 'dropdown', 'enum', 'switch', 'checkbox', 'grid', 'attachments', 'nullableboolean', 'multiinteger', 'custom']),
  inputProps: PropTypes.any,
  readonly: PropTypes.bool,
  emptyValue: PropTypes.any,
  addButtonLabel: PropTypes.string,
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
  onAddItem: PropTypes.func,
  onRemoveItem: PropTypes.func,
  showGridDeleteButton: PropTypes.func,
  detailUrl: PropTypes.func,
  dataSource: function (props, propName) {
    if ((props['inputType'] === inputTypes.lookup &&
      (props[propName] === undefined || typeof (props[propName]) !== 'function'))) {
      return new Error('Please provide a dataSource function!')
    }
  },
  /**
   * Used to apply custom filter to dataSource
   */
  filter: PropTypes.any,
  getPath: PropTypes.func
}

PropertyRenderer.propTypes = RendererPropTypes

function PropertyRenderer (props) {
  const inputProps = useRefMemo(props.inputProps)
  const formProps = useContext(FormContext)
  const isReadOnly = useContext(PermissionSectionContext)
  const { uid } = formProps
  const mode = isReadOnly ? modes.view : formProps.mode
  const actions = useDispatch(mapDispatchToProps)

  const mappedProps = useReduxState(
    function mapStateToProps (state) {
      const { name, inputType, editTemplate, readonly } = props
      const namePath = getPath(name)
      const { forms } = state
      const target = props.target
      const origPath = getDataPath(forms, uid, namePath, 'originalData')
      const originalValue = toJs(forms.getIn(origPath))
      const valuePath = getDataPath(forms, uid, namePath)
      const value = toJs(forms.getIn(valuePath))
      const approvedPath = getDataPath(forms, uid, namePath, 'approvedValues')
      const approvedValue = toJs(forms.getIn(approvedPath))
      return {
        ...props,
        inputProps,
        mode: readonly ? modes.view : mode,
        value: value,
        originalValue: originalValue,
        approvedValue: approvedValue,
        inputType: editTemplate ? inputTypes.custom : (inputType || inputTypes.text),
        locale: state.localization.locale,
        name: namePath.join('.'),
        target,
        getTarget: getPath
      }
    }, isEqual)
  return <ComplexPropsHandler {...formProps} {...mappedProps} {...actions} />
}

function ComplexPropsHandler (props) {
  const value = useRefMemo(props.value)
  const originalValue = useRefMemo(props.originalValue)
  const filter = useRefMemo(props.filter)
  const detailDialog = useRefMemo(props.detailDialog)
  const defaultSort = useRefMemo(props.defaultSort)
  return <PurePropertyRenderer
    {...props}
    value={value}
    originalValue={originalValue}
    filter={filter}
    detailDialog={detailDialog}
    defaultSort={defaultSort}
  />
}

export function getPath (name) {
  return name.split('.')
}

const mapDispatchToProps = {
  onValueChange: actionCreators.change,
  onAddItem: actionCreators.addItem,
  onRemoveItem: actionCreators.removeItem,
  onDeleteItem: actionCreators.deleteItem,
  onReviewButtonsToggle: actionCreators.toggleReviewButtons,
  onApprovalChange: actionCreators.changeApproval,
  openDialog: dialogActions.openDialog
}

const PurePropertyRenderer = React.memo(function PurePropertyRenderer (props) {
  const { mode, inputType, readonly, isChangeItemId } = props
  const info = useApprovalInfo(props)

  if (inputType === inputTypes.grid) {
    return <ApprovalGridRenderer {...props} />
  }
  if (inputType === inputTypes.attachments) {
    return <ApprovalAttachmentsRenderer {...props} />
  }
  if (!readonly) {
    switch (mode) {
      case modes.edit:
        return renderExtendedEditRenderer({ ...props, info })
      case modes.view:
      case modes.approval:
        if (isChangeItemId) {
          return <DefaultReviewRenderer {...props} />
        }
        break
      default:
        break
    }
  }
  return renderDefaultViewRenderer(props)
})

function renderDefaultViewRenderer (props, valueOverride) {
  const emptyValue = props.emptyValue || EMPTY_VALUE
  const val = typeof valueOverride === 'undefined'
    ? (props.value !== undefined && props.value !== null
      ? props.value
      : props.originalValue)
    : valueOverride
  if (!val && props.inputType !== inputTypes.switch && props.inputType !== inputTypes.checkbox && props.inputType !== inputTypes.number && props.inputType !== inputTypes.enum && props.inputType !== inputTypes.nullableboolean) return emptyValue
  switch (props.inputType) {
    case inputTypes.enum:
      return <Resx render={({ localize }) => getEnumTranslation({ localize, enumName: props.inputProps.enumName, value: val || props.defaultEnumValue })} />
    case inputTypes.lookup:
    case inputTypes.dropdown:
      return <Name data={val} />
    case inputTypes.switch:
    case inputTypes.checkbox:
      return <Resx name={val ? 'Common.Yes' : 'Common.No'} />
    case inputTypes.date:
      return formatDate(val)
    case inputTypes.number:
      return formatNumber(val, props.inputProps)
    case inputTypes.nullableboolean:
      return <Resx name={props.value === true ? 'Common.Yes' : (props.value === false ? 'Common.No' : 'Common.NoValue')} />
    case inputTypes.multiinteger:
      return val.replaceAll(',', ', ')
    default:
      return val || emptyValue // TODO: number and date formatting
  }
}

function renderExtendedEditRenderer (props) {
  const { required, regex, onValidate, name, value, maxLength, serverValidation, disabled, info, originalValue, approvedValue } = props
  if (required || regex || onValidate || maxLength || serverValidation) {
    if (typeof serverValidation === 'function') {
      return (
        <Approval
          approved={info.approved}
          validating
          value={value}
          approvedValue={approvedValue}
          originalValue={originalValue}
          onRevert={handleRevert(props)}>
          <ServerValidator
            name={name}
            value={value}
            required={required}
            regex={regex}
            onValidate={onValidate}
            maxLength={maxLength}
            serverValidation={serverValidation}>
            {renderDefaultEditRenderer(props)}
          </ServerValidator>
        </Approval>
      )
    }
    return (
      <Approval
        approved={info.approved}
        validating
        value={value}
        approvedValue={approvedValue}
        originalValue={originalValue}
        onRevert={handleRevert(props)}>
        <Validator
          name={name}
          value={value}
          disabled={disabled}
          required={required}
          regex={regex}
          onValidate={onValidate}
          maxLength={maxLength}>
          {renderDefaultEditRenderer(props)}
        </Validator>
      </Approval>
    )
  }
  return (
    <Approval
      approved={info.approved}
      needApproval={info.needApproval}
      validating={false}
      value={value}
      approvedValue={approvedValue}
      originalValue={originalValue}
      onRevert={handleRevert(props)}>
      {renderDefaultEditRenderer(props)}
    </Approval>
  )
}

function renderDefaultEditRenderer (props) {
  const { uid, name, inputType, value, dataSource, disabled, filter, inputProps, clientSide, defaultEnumValue, isCodeList, originalValue, maxValue } = props
  switch (inputType) {
    case inputTypes.number:
      const inpProps = { ...inputProps || {} }
      inpProps.maxValue = maxValue
      return <NumericInput value={value} disabled={disabled} onChange={handleOnChange(props, e => e)} {...inpProps} />
    case inputTypes.date: {
      return <DatePicker value={value} disabled={disabled} onChange={handleOnChange(props, val => val)} {...inputProps || {}} />
    }
    case inputTypes.enum: {
      return (
        <EnumDropDownList enumDefinition={inputProps} disabled={disabled} value={isDefined(value) ? value : defaultEnumValue} onChange={handleOnChange(props, ({ value }) => value)} allowClear />
      )
    }
    case inputTypes.lookup: {
      return <DropDownList
        dataSource={dataSource}
        filter={lookupDataFilter}
        filterData={filter ? filter.id : null}
        value={value || null}
        onChange={handleOnChange(props, ({ item }) => item)}
        allowClear
        clientSide={clientSide}
        isCodeList={isCodeList}
        disabled={disabled}
        originalValue={originalValue && originalValue.id ? originalValue.id : null} />
    }
    case inputTypes.dropdown: {
      return <DropDownList
        dataSource={dataSource}
        value={value}
        onChange={handleOnChange(props, ({ item }) => item)}
        clientSide={clientSide}
        disabled={disabled}
        isCodeList={isCodeList} />
    }
    case inputTypes.switch:
    case inputTypes.checkbox: {
      return <CheckBox id={`${name}_${uid}`} disabled={disabled} checked={value || false} title={props.title} onChange={handleOnChange(props, e => e)} />
    }
    case inputTypes.textarea:
      return <Input value={value || ''} disabled={disabled} type='textarea' onChange={handleOnChange(props, e => e.target.value)} {...inputProps || {}} />
    case inputTypes.nullableboolean:
      return <NullableBooleanDropDownList value={value} disabled={disabled} onChange={handleOnChange(props, e => e.value)} {...inputProps} />
    case inputTypes.multiinteger:
      return <MultiIntegerInput value={value || ''} disabled={disabled} onChange={handleOnChange(props, e => e)} {...inputProps || {}} />
    default:
      return <Input value={value || ''} disabled={disabled} onChange={handleOnChange(props, e => e.target.value)} {...inputProps || {}} />
  }
}

function handleRevert (props) {
  const { originalValue } = props
  return handleOnChange(props, () => originalValue)
}

function handleOnChange (props, getValue) {
  const { uid, name, onValueChange, onChange } = props
  return event => {
    const value = getValue(event)
    let additionalChanges = null
    if (typeof onChange === 'function') {
      additionalChanges = onChange(props, value)
    }
    onValueChange(uid, name, value, additionalChanges)
  }
}

function lookupDataFilter (data, filter) {
  return data.filter((item) => !item || !filter || item.navigationId === filter)
}

function DefaultReviewRenderer (props) {
  const info = useApprovalInfo(props)
  const [originalApproved, setOriginalApproved] = useState(info.approved)
  const { uid, onApprovalChange, approvedValue, originalValue, value } = props
  const onApproveClick = useCallback(() => {
    setOriginalApproved(null)
    onApprovalChange(uid, info.approvalName, true, value)
  }, [info.approvalName, onApprovalChange, uid, value])
  const onRejectClick = useCallback(() => {
    setOriginalApproved(null)
    onApprovalChange(uid, info.approvalName, false, value)
  }, [info.approvalName, onApprovalChange, uid, value])
  const approvedValueIsSame = valueFallbackComparer(approvedValue, value)
  const needsApproval = (typeof info.approved !== 'boolean') || (!approvedValueIsSame && info.approved === originalApproved)
  const wrapClass = {
    'v-edit--need-approve': needsApproval,
    'v-edit--approved': info.approved === true && !needsApproval,
    'v-edit--denied': info.approved === false && !needsApproval
  }
  if (info.needApproval) {
    const renderedValue = renderDefaultViewRenderer(props, value !== undefined && value !== null ? value : null)
    const rendererOriginalValue = renderDefaultViewRenderer(props, originalValue !== undefined && originalValue !== null ? originalValue : null)
    return (
      <div className={classNames('v-edit', wrapClass)}>
        <div className='v-edit__value'>{renderedValue}</div>
        <div className={classNames('v-edit__box')}>
          <button className='v-edit__trigger' />
          <div className='v-edit__pop-up'>
            <div className='v-edit__pop-up-holder'>
              <div className='v-edit__compaire'>
                <div className='v-edit__c-left'>
                  <small><Resx name='Common.PreviousValue' /></small>
                  <strong>{rendererOriginalValue}</strong>
                </div>
                <div className='v-edit__c-right'>
                  <small><Resx name='Common.ForApproval' /></small>
                  <strong>{renderedValue}</strong>
                </div>
              </div>
              {context.isInternalApp() && <PermissionRequired name={`Scopes.${props.scope}.Approve`}>
                <div className='v-edit__actions'>
                  <button
                    onClick={onApproveClick}
                    disabled={(info.approving || info.approved || false) && !needsApproval}
                    className='btn btn-success btn-xs'>
                    <Resx name='Common.Checked' />
                  </button>
                  <button
                    onClick={onRejectClick}
                    disabled={(info.approving || info.approved === false || false) && !needsApproval}
                    className='btn btn-danger btn-xs'>
                    <Resx name='Common.Incorrect' />
                  </button>
                </div>
              </PermissionRequired>}
            </div>
          </div>
        </div>
      </div>
    )
  }
  return renderDefaultViewRenderer(props)
}

function ApprovalAttachmentsRenderer (props) {
  return ApprovalGridRenderer({ isFileAttachmentGrid: true, ...props })
}

const handleUploadAttachmentsChange = (props) => (attachments) => {
  const currentAttachments = [...(props.value || []), ...attachments.map(x => markAsFileAttachment(x))]
  handleOnChange(props, e => e)(currentAttachments)
}

function ApprovalGridRenderer (props) {
  const { localize } = useLocalization()
  const isGranted = usePermissionChecker()
  const info = useApprovalInfo(props)
  const { uid, targetId, name, mode, value, dataSource, onRemoveItem, onDeleteItem, addButtonLabel, children, isChangeItemId, approvalInfo, approvalName, approvedValue, showGridDeleteButton, defaultSort, defaultValue,
    isFileAttachmentGrid, simpleView, attachmentsView, onApprovalChange } = { ...props, ...info }
  const items = (value && typeof value.length === 'number') ? value : []
  let showDbData = !!targetId // not insert
  const hasApprovalChanges = (item, index) => {
    if (!approvalInfo || !approvalInfo.size) return false
    // Check by index first (primary match)
    const prefix = `${approvalName}[${index}]`
    if (approvalInfo.some((_, key) => key.startsWith(prefix))) return true
    // Fallback: check if any approval entry exists for this item's id in the array
    // This handles cases where array order changed after merge
    if (item.id > 0) {
      return approvalInfo.some((_, key) => {
        const match = key.match(new RegExp(`^${approvalName}\\[(\\d+)\\]`))
        if (match) {
          const approvalIndex = parseInt(match[1], 10)
          const itemAtApprovalIndex = items[approvalIndex]
          return itemAtApprovalIndex && itemAtApprovalIndex.id === item.id
        }
        return false
      })
    }
    return false
  }
  const modifiedItems = items.filter((x, index) => isModified(x) || x.id < 0 || isDeletedItem(x) || hasApprovalChanges(x, index))
  let showChanges = isChangeItemId && modifiedItems.length > 0

  const [originalApproved, setOriginalApproved] = useState(info.approved)
  const onApproveClick = useCallback((_, dataIndex) => {
    setOriginalApproved(null)
    onApprovalChange(uid, `${info.approvalName}[${dataIndex - 1}]`, true, value)
  }, [info.approvalName, onApprovalChange, uid, value])
  const onRejectClick = useCallback((_, dataIndex) => {
    setOriginalApproved(null)
    onApprovalChange(uid, `${info.approvalName}[${dataIndex - 1}]`, false, value)
  }, [info.approvalName, onApprovalChange, uid, value])
  const approvedValueIsSame = valueFallbackComparer(approvedValue, value)
  const needsApproval = (typeof info.approved !== 'boolean') || (!approvedValueIsSame && info.approved === originalApproved)

  let sourceActionButtons = []
  let modificationActionButtons = []
  if (!props.readonly) {
    if (!isFileAttachmentGrid) {
      sourceActionButtons.push({
        readonlybutton: mode === modes.view ? 'true' : 'false',
        icon: item => mode === modes.edit && !isDeletedItem(items.find(x => x.id === item.id)) ? 'pencil' : 'show',
        onClick: item => {
          const modifiedItem = items.find(x => x.id === item.id)
          openDialog(props, modifiedItem || item, !modifiedItem)
        }
      })

      modificationActionButtons = [{
        readonlybutton: mode === modes.view ? 'true' : 'false',
        icon: item => mode === modes.edit && !isDeletedItem(item) ? 'pencil' : 'show',
        onClick: item => openDialog(props, item, false, items.indexOf(item))
      }]
    } else if (mode === modes.approval && isGranted(`Scopes.${props.scope}.Approve`)) {
      modificationActionButtons.push({
        readonlybutton: (info.approving || info.approved || false) && !needsApproval,
        icon: 'bxs-check-circle success',
        title: localize('Common.Approve'),
        onClick: onApproveClick
      })
      modificationActionButtons.push({
        readonlybutton: (info.approving || info.approved === false || false) && !needsApproval,
        icon: 'bxs-no-entry danger',
        title: localize('Common.Reject'),
        onClick: onRejectClick
      })
    }
    if (mode === modes.edit) {
      sourceActionButtons.push({ show: showGridDeleteButton, onDelete: item => onDeleteItem(uid, name, item) })
      modificationActionButtons.push({
        onDelete: item => onRemoveItem(uid, name, item),
        icon: 'undo red' })
    }
  } else {
    if (props.detailDialog) {
      sourceActionButtons.push({
        readonlybutton: 'true',
        icon: 'show',
        onClick: item => openDialog(props, item, true)
      })
    }
    if (props.detailUrl) {
      sourceActionButtons.push({
        readonlybutton: 'true',
        icon: 'link',
        url: props.detailUrl,
        show: (item) => !!props.detailUrl(item)
      })
    }
  }
  let columns = React.Children.toArray(children)
  const sourceColumns = [...columns]
  if (sourceActionButtons.length) {
    sourceColumns.push(<Column key='commands' {...actionButtonsColumnProps(sourceActionButtons)} />)
  }
  columns.push(<Column key='commands' {...actionButtonsColumnProps(modificationActionButtons)} />)

  if (props.isChangeItemId) {
    columns.unshift(<Column key='modificationInfo' width={100} cell={createModificationInfoCell(approvalInfo, approvalName, items)} sortable={false} filterable={false} />)
    if (props.mode !== modes.edit) {
      columns.unshift(<Column key='approvalStatus' width={100} cell={createApprovalStatusCell(approvalInfo, approvalName, items)} sortable={false} filterable={false} />)
    }
    if (showChanges) {
      sourceColumns.unshift(<Column key='modificationInfo' width={100} cell={createModificationInfoSourceCell(items, approvalInfo, approvalName)} sortable={false} filterable={false} />)
    }
  }
  return (
    <React.Fragment>
      <div className='action-row'>
        {mode === modes.edit && !isFileAttachmentGrid && <button className='btn btn-sm btn-primary' onClick={() => openDialog(props, defaultValue || null)}>
          <Icon name={changeChangeItemIcon} />
          <span><Resx name={addButtonLabel} /></span>
        </button>}
        {mode === modes.edit && isFileAttachmentGrid && <UploadAttachments dropzoneFullWidth={attachmentsView} onChange={handleUploadAttachmentsChange(props)} />}
      </div>
      {showChanges && <div className='edited-part'>
        <div className='edited-part__title'><Resx name='Common.ModifiedItems' /></div>
        <BsrsGrid items={modifiedItems} defaultSort={defaultSort} simpleView={simpleView} filterable={false}>{columns}</BsrsGrid>
      </div>}
      {showDbData && <React.Fragment>
        {showChanges &&
        <div className='edited-part__title'><Resx name='Common.ExistingItems' /></div>
        }
        <BsrsGrid dataSource={dataSource} defaultSort={defaultSort} simpleView={simpleView}>
          {sourceColumns}
        </BsrsGrid>
      </React.Fragment>}
    </React.Fragment>
  )
}

function isDeletedItem (item) {
  return !!(item && item.isDeleted)
}

function openDialog (props, value, isOriginal, index) {
  const { id, uid, name, onAddItem, openDialog, detailDialog, mode, changeItemId, isChangeItemId } = props
  let originalValue = value
  let approvedValue = value
  value = value || { __isCreated: true }
  const itemId = value.id

  if (itemId > 0 && props.originalValue && props.originalValue.length > 0) {
    const originalItem = props.originalValue.find(x => x.id === itemId)
    if (originalItem) originalValue = originalItem
  }

  if (itemId > 0 && props.approvedValue && props.approvedValue.length > 0) {
    const approvedItem = props.approvedValue.find(x => x.id === itemId)
    if (approvedItem) approvedValue = approvedItem
  }

  const loader = typeof props.detailDialog.loadData === 'function' && !!isOriginal
    ? (id) => props.detailDialog.loadData(id)
    : () => new Promise(resolve => resolve({ value, originalValue, approvedValue, __dialogLoader: true }))
  const tempUid = '#' + uuid()
  openDialog({
    ...detailDialog,
    uid: tempUid,
    parentUid: uid,
    isOriginal: !!isOriginal,
    mode: mode,
    loader,
    id: id || value.id,
    changeItemId,
    isChangeItemId,
    target: typeof index === 'number' ? `${name}[${index}]` : null,
    onAction: mode === modes.edit ? () => onAddItem(uid, name, tempUid) : null,
    value
  })
}

export function toJs (obj) {
  const isList = List.isList(obj)
  if (isList || Map.isMap(obj)) {
    obj = obj.toJS()
  }
  return obj
}

export {
  PropertyRenderer,
  RendererPropTypes
}



// WEBPACK FOOTER //
// ./src/components/changeRequests/propertyRenderer.js